import { Stack, useTheme } from "@mui/material";
import { FoodsIcon, FoodsIconButton, Typography } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import { SsoProtocol } from "src/pages/Authentication/ssoConnector";

type CallbackUrlPanelProps = {
    callbackUrl: string;
    onCopy: () => void;
    protocol: SsoProtocol;
};

export const CallbackUrlPanel = ({ callbackUrl, onCopy, protocol }: CallbackUrlPanelProps) => {
    const theme = useTheme();
    const { t } = useTranslation();

    const title =
        protocol === "saml2" ?
            t("settings.sso.providers.callback_panel.title_saml2", "Assertion consumer URL")
        :   t("settings.sso.providers.callback_panel.title", "Callback URL");

    return (
        <Stack
            gap={theme.spacing(1)}
            sx={{
                padding: theme.spacing(2.5),
                borderRadius: theme.spacing(1.5),
                backgroundColor: theme.custom.grey[300],
            }}
        >
            <Stack flexDirection="row" alignItems="center" gap={theme.spacing(1)}>
                <FoodsIcon icon="information" size={2.5} />
                <Typography variant="body-bold">{title}</Typography>
            </Stack>
            <Typography variant="small-medium" sx={{ color: theme.custom.grey[2800] }}>
                {t(
                    "settings.sso.providers.callback_panel.help",
                    "Declare this URL on your identity provider before saving.",
                )}
            </Typography>
            {callbackUrl === "" ?
                <Typography variant="small-medium" sx={{ color: theme.custom.grey[2000] }}>
                    {t(
                        "settings.sso.providers.callback_panel.pending",
                        "It is built from the provider name, so fill that in first.",
                    )}
                </Typography>
            :   <Stack
                    flexDirection="row"
                    alignItems="center"
                    justifyContent="space-between"
                    gap={theme.spacing(1)}
                    sx={{
                        padding: `${theme.spacing(1)} ${theme.spacing(1.5)}`,
                        borderRadius: theme.spacing(1),
                        backgroundColor: theme.custom.grey[100],
                        border: `1px solid ${theme.custom.grey[500]}`,
                    }}
                >
                    <Typography variant="small-medium" sx={{ wordBreak: "break-all" }}>
                        {callbackUrl}
                    </Typography>
                    <FoodsIconButton
                        size="xs"
                        icon="duplicate"
                        tooltip={{ text: t("settings.sso.providers.copy_callback_url", "Copy callback URL") }}
                        buttonProps={{
                            "aria-label": t("settings.sso.providers.copy_callback_url", "Copy callback URL"),
                            onClick: onCopy,
                        }}
                    />
                </Stack>
            }
        </Stack>
    );
};
